// The Library's Home (LibraryHomePage on the Mac): a greeting by the hour, where the writer left off in this browser
// (the chapter and the last sentence before the caret; the Mac's Apple Intelligence line is Apple-only), the project
// card with its top-level parts as one bar against the manuscript target (a journal's posts by status, the last one out
// and the next to go) and the way back in, this week's bars and sentence, and the year in ink. Also Write with nothing open (NothingOpenView) and the year grid both pages share.
import { useEffect, useMemo, useState } from 'react';
import type { DailyWords, Project } from '@writing-suite/core';
import {
  greeting, journalCount, journalProgress, lastSentence, partProgress, partsNoun, placeName, projectKind, week as weekOf, type JournalPost, type JournalSummary,
  type LeftOff,
} from '../../../core/src/home/index.ts';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { useApp } from '../shell/context.ts';
import { followLink, formatRoute } from '../shell/route.ts';
import { ownDaily } from '../store/members.ts';
import { useWorkspace } from '../shell/workspace.ts';
import { authorName } from '../store/useProject.ts';
import { leftOffUTF16, loadLeftOff } from '../store/leftOff.ts';
import type { LiveProject } from '../store/project.ts';
import { projectTitle } from '../store/library.ts';
import { activityGrid, formatNumber, formatWords } from './helpers.ts';

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const NARROW_DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'narrow' });
const WIDE_DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'long' });
const POST_DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' });
const POST_DAY_YEAR = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/** "3 October", with the year when it isn't this one. */
const postDay = (d: Date) => (d.getFullYear() === new Date().getFullYear() ? POST_DAY : POST_DAY_YEAR).format(d);

/** A status's share of a journal's bar: published in ink, scheduled in terracotta, drafts and ideas fainter. */
const STATUS_TONE: Record<string, string> = {
  'status-published': 'published', 'status-scheduled': 'scheduled', 'status-draft': 'draft', 'status-idea': 'idea',
};

/** A journal's posts by status, the last one out and the next to go (`journalFigures`; a blog has no word target). */
function JournalFigures({ project, journal, words, writeHref }: { project: Project; journal: JournalSummary; words: number; writeHref: string }) {
  const counts = [`${formatNumber(journal.posts)} ${journal.posts === 1 ? 'post' : 'posts'}`, ...journal.counts.map((c) => journalCount(project, c, formatNumber))];
  return (
    <div className="home-parts" data-journal-posts={journal.posts}>
      <span className="part-bar" role="img" aria-label={counts.join(', ')}>
        {journal.counts.map((c, i) => (
          <span
            key={i}
            className={`part-fill status-${(c.status && STATUS_TONE[c.status]) || 'other'}`}
            ref={(el) => el?.style.setProperty('--fraction', String(c.count / Math.max(1, journal.posts)))}
          />
        ))}
      </span>
      <p className="home-parts-line">
        <span className="home-parts-names">{counts.join(' · ')}</span>
        <span className="num" data-manuscript-words={words}>{formatWords(words)}</span>
      </p>
      {journal.lastPublished ? (
        <PostLine lead={journal.lastPublished.date ? `Last published ${postDay(journal.lastPublished.date)}` : 'Last published'} post={journal.lastPublished} href={writeHref} which="last" />
      ) : null}
      {journal.next ? (
        <PostLine
          lead={journal.next.status === 'status-scheduled' ? (journal.next.date ? `Scheduled for ${postDay(journal.next.date)}` : 'Scheduled') : 'Next draft'}
          post={journal.next}
          href={writeHref}
          which="next"
        />
      ) : null}
    </div>
  );
}

/** "Last published 3 October: *Submitting*", the title opening the post in Write. */
function PostLine({ lead, post, href, which }: { lead: string; post: JournalPost; href: string; which: 'last' | 'next' }) {
  const ws = useWorkspace();
  return (
    <p className="home-post" data-post={which} data-fragment={post.id}>
      <span className="home-post-lead">{lead}:</span>{' '}
      <a
        className="home-post-title"
        href={href}
        title="Open in Write"
        onClick={(e) => {
          if (!ws) return followLink(e, href);
          e.preventDefault();
          ws.reveal({ fragmentId: post.id, from: 0, to: 0 });
        }}
      >
        {post.title}
      </a>
    </p>
  );
}

/** The account's name: as last seen here at once, then as the relay has it. */
export function useAccountName(): string | null {
  const app = useApp();
  const [name, setName] = useState<string | null>(() => authorName());
  useEffect(() => {
    let live = true;
    void app.account.session
      .account()
      .then((info) => {
        if (!live || !info.name) return;
        setName(info.name);
        try {
          localStorage.setItem('ws:authorName', info.name);
        } catch {
          // this page only
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [app.account.session]);
  return name;
}

/** Where the writer left off here, with the place and the sentence before the caret. */
export function useLeftOff(projectId: string, project: Project, live: LiveProject | null) {
  return useMemo(() => {
    const textOf = (id: string) => live?.text(id) ?? project.fragment(id)?.text ?? null;
    const leftOff = loadLeftOff(projectId, project, textOf);
    if (!leftOff) return null;
    const text = textOf(leftOff.fragmentId) ?? '';
    return { leftOff, place: placeName(project, leftOff.fragmentId), sentence: lastSentence(text, leftOff.offset), utf16: leftOffUTF16(leftOff, text) };
  }, [projectId, project, live]);
}

/** Opens Write where the writer left off, caret and all (`returnToWriting`). */
function useReturn(left: { leftOff: LeftOff; utf16: number } | null, writeHref: string) {
  const ws = useWorkspace();
  return (e: { preventDefault(): void; button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }) => {
    if (left && ws) {
      e.preventDefault();
      ws.reveal({ fragmentId: left.leftOff.fragmentId, from: left.utf16, to: left.utf16 });
    } else followLink(e, writeHref);
  };
}

export function LibraryHomePage({ projectId, project, live, hrefFor }: PageProps) {
  const app = useApp();
  const name = useAccountName();
  const left = useLeftOff(projectId, project, live);
  const listed = app.projects?.find((p) => p.id === projectId);
  const title = listed ? projectTitle(listed) : project.title;
  const journal = useMemo(() => (project.type === 'journal' ? journalProgress(project) : null), [project]);
  const bar = useMemo(() => (journal ? [] : partProgress(project)), [project, journal]);
  const words = useMemo(() => project.manuscriptWords(), [project]);
  const targets = useMemo(() => project.targets(), [project]);
  // A shared project's week is your own words (W9c), as everywhere the Library speaks to you.
  const daily = useMemo(() => ownDaily(project, live), [project, live]);
  // Back to writing where you left off; a project with nothing written yet starts in Plan.
  const startsInPlan = !left && words === 0;
  const writeHref = hrefFor(startsInPlan ? 'plan' : 'write', null);
  const statsHref = hrefFor('library/statistics', null);
  const back = useReturn(left, writeHref);
  // Go to Project: Write at the manuscript's first item, for going on from the top of the binder (`goToProject`).
  const firstHref = useMemo(() => {
    const manuscript = project.manuscriptId();
    const first = manuscript ? project.tree(manuscript).find((n) => !n.fragmentId || project.fragment(n.fragmentId)?.trashed !== true) : undefined;
    return formatRoute({ name: 'project', projectId, room: 'write', page: 'write', nodeId: first?.id ?? null, arrangementId: null, params: {} });
  }, [project, projectId]);
  const [now] = useState(() => new Date());
  const thisWeek = weekOf(daily, now);
  const most = Math.max(1, ...thisWeek.days.map((d) => d.words), targets.session ?? 0);
  const today = thisWeek.days.at(-1)!.day;

  return (
    <div className="home-page" data-state="library-home">
      <header className="home-greeting">
        <h1 tabIndex={-1}>{greeting(name, now)}</h1>
        {left ? (
          <>
            <p className="home-left-off" data-left-off-place={left.place} data-left-off-fragment={left.leftOff.fragmentId}>
              You left off in <em>{left.place}</em>.
            </p>
            {left.sentence ? <p className="home-sentence">“{left.sentence}”</p> : null}
          </>
        ) : null}
      </header>

      <div className="home-row">
        <section className="home-card" aria-labelledby="home-project">
          <div className="home-card-head">
            <h2 id="home-project">{title}</h2>
            <span className="home-kind">{projectKind(project.type)}</span>
          </div>
          {journal ? (
            <JournalFigures project={project} journal={journal} words={words} writeHref={writeHref} />
          ) : (
            <div className="home-parts">
              <span
                className="part-bar"
                role="img"
                aria-label={bar.map((p) => `${p.title || 'Untitled'}, ${formatWords(p.words)}`).join('; ') || 'Nothing written yet'}
              >
                {bar.map((p, i) => (
                  <span
                    key={i}
                    className={`part-fill tone-${i % 3}`}
                    title={`${p.title || 'Untitled'}: ${formatWords(p.words)}`}
                    ref={(el) => el?.style.setProperty('--fraction', String(p.fraction))}
                  />
                ))}
              </span>
              <p className="home-parts-line">
                <span className="home-parts-names">{bar.length <= 5 ? bar.map((p) => p.title || 'Untitled').join(' · ') : `${formatNumber(bar.length)} ${partsNoun(project)}`}</span>
                <span className="num" data-manuscript-words={words}>
                  {targets.manuscript ? `${formatNumber(words)} of ${formatNumber(targets.manuscript)}` : formatWords(words)}
                </span>
              </p>
              {targets.manuscript ? null : (
                <a className="home-link" href={statsHref} onClick={(e) => followLink(e, statsHref)}>
                  Set a target
                </a>
              )}
            </div>
          )}
          <div className="home-actions">
            {left ? (
              <a className="pill-button outline" href={firstHref} onClick={(e) => followLink(e, firstHref)} data-go-to-project>
                Go to Project
              </a>
            ) : null}
            <a className="pill-button" href={writeHref} onClick={back} data-return>
              {left ? `Return to ${left.place}` : startsInPlan ? 'Start planning' : 'Start writing'}
            </a>
          </div>
        </section>

        <section className="home-week" aria-labelledby="home-week">
          <h2 id="home-week" className="eyebrow">
            This week
          </h2>
          <ol className="home-week-bars">
            {thisWeek.days.map((d) => (
              <li key={d.day} aria-label={`${WIDE_DAY.format(d.date)}: ${formatWords(d.words)}`}>
                <span
                  className={`home-week-bar${d.day === today ? ' today' : d.words > 0 ? ' wrote' : ''}`}
                  ref={(el) => el?.style.setProperty('--height', `${Math.max(3, (d.words / most) * 90)}px`)}
                />
                <span className="home-week-day" aria-hidden="true">
                  {NARROW_DAY.format(d.date)}
                </span>
              </li>
            ))}
          </ol>
          <p className="home-week-sentence">{thisWeek.sentence}</p>
        </section>
      </div>

      <section className="home-year" aria-labelledby="home-year">
        <h2 id="home-year" className="eyebrow">
          The year so far
        </h2>
        <ActivityGrid daily={daily} target={targets.session} ink />
      </section>

      <p>
        <a className="home-link" href={statsHref} onClick={(e) => followLink(e, statsHref)}>
          All statistics →
        </a>
      </p>
    </div>
  );
}

/** The year's writing (ActivityGrid): 53 Monday-first weeks, shaded by the daily target or quartiles. `ink` is the
 *  Home's plain ink version (no legend or month labels). */
export function ActivityGrid({ daily, target, ink = false, year = null }: { daily: DailyWords[]; target: number | null; ink?: boolean; year?: number | null }) {
  const grid = activityGrid(daily, target, new Date(), year);
  return (
    <>
      <div className="grid-wrap">
        <div
          className={`activity-grid${ink ? ' ink' : ''}`}
          role="img"
          aria-label={`Writing on each day of ${year === null ? 'the last year' : year}: ${formatWords(grid.year)} in all.`}
          data-grid-year={year ?? 'last'}
        >
          {grid.weeks.map((w, i) => {
            const first = w.find((d) => d);
            const monthStart = first && Number(first.day.slice(8)) <= 7;
            return (
              <div className="grid-week" key={i}>
                {ink ? null : (
                  <span className="grid-month" aria-hidden="true">
                    {monthStart && first ? MONTH.format(new Date(`${first.day}T12:00:00`)) : ''}
                  </span>
                )}
                {w.map((d, j) =>
                  d ? (
                    <span key={j} className={`grid-day level-${d.level}`} title={`${DAY.format(new Date(`${d.day}T12:00:00`))}: ${formatWords(d.words)}`} />
                  ) : (
                    <span key={j} className="grid-day future" />
                  ),
                )}
              </div>
            );
          })}
        </div>
      </div>
      {ink ? null : (
        <p className="grid-legend" aria-hidden="true">
          Less <span className="grid-day level-0" /> <span className="grid-day level-1" /> <span className="grid-day level-2" /> <span className="grid-day level-3" />{' '}
          <span className="grid-day level-4" /> More
        </p>
      )}
    </>
  );
}

/** Write with nothing to show (NothingOpenView): the way back to where the writer left off, or where to start. */
export function NothingOpen({ projectId, project, live, note }: { projectId: string; project: Project; live: LiveProject | null; note?: string }) {
  const left = useLeftOff(projectId, project, live);
  const ws = useWorkspace();
  const item = project.type === 'journal' ? 'post' : 'scene';
  return (
    <div className="nothing-open" data-nothing-open>
      {note ? <p className="empty-note">{note}</p> : null}
      {left ? (
        <>
          <p className="nothing-open-line">
            You left off in <em>{left.place}</em>.
          </p>
          <button type="button" className="pill-button" onClick={() => ws?.reveal({ fragmentId: left.leftOff.fragmentId, from: left.utf16, to: left.utf16 })}>
            Return to {left.place}
          </button>
          <p className="nothing-open-hint">Or choose a {item} on the shelf.</p>
        </>
      ) : (
        <p className="nothing-open-line">Choose a {item} on the shelf to write.</p>
      )}
    </div>
  );
}
